import { makeObservable, computed, action, observable, reaction } from 'mobx'
import { type Address, type NormalizedAddress } from './address.types'
import { normalizeAddress, denormalizeAddress } from './address.utils'
import { ADDRESSES_KEY, ADDRESSES_VALIDATION_SKIP_KEY } from './address.consts'
import { COUNTRIES } from '../../config/countries-data'
import { analytics } from '../../services/Analytics/Analytics'
import { logger } from '../../services/logger'
import UserManager from '../../services/UserManager'
import StorageManager from '../../services/StorageManager'
import { isServer } from 'utils/runtimeUtils'

export class AddressStore {
  currentAddress: Address | null = null

  addressValidationSkipped: boolean | null = null

  constructor() {
    makeObservable(this, {
      currentAddress: observable,
      addressValidationSkipped: observable,
      currentNormalizedAddress: computed,
      shouldValidateAddressInServer: computed,
      setCurrentAddress: action,
      setAddressValidationSkipped: action,
      removeAddressIfNotSupported: action,
      clearData: action,
      setAddressFromServer: action,
    })
    if (isServer()) {
      return
    }

    const storedAddress = StorageManager.get(ADDRESSES_KEY)
    const storedSkip = StorageManager.get<boolean>(
      ADDRESSES_VALIDATION_SKIP_KEY
    )

    if (storedAddress) {
      this.currentAddress = storedAddress as Address
    }
    this.addressValidationSkipped = storedSkip

    reaction(
      () => this.currentAddress,
      (address: Address | null) => {
        StorageManager.set(ADDRESSES_KEY, address)
      }
    )

    reaction(
      () => this.currentAddress?.phoneNumber,
      (phoneNumber) => {
        if (phoneNumber) {
          analytics.syncPhoneToAdNetworks(phoneNumber)
        }
      },
      { fireImmediately: true }
    )

    reaction(
      () => this.addressValidationSkipped,
      (skip: boolean | null) => {
        StorageManager.set(ADDRESSES_VALIDATION_SKIP_KEY, skip as any)
      }
    )
  }

  get currentNormalizedAddress(): NormalizedAddress | null {
    return normalizeAddress(
      this.currentAddress,
      !!this.addressValidationSkipped
    )
  }

  get shouldValidateAddressInServer(): boolean {
    return !this.addressValidationSkipped
  }

  setCurrentAddress = (address: Address, persist: boolean = true): void => {
    if (address?.fullName) {
      UserManager.setUserFullName(address.fullName, persist)
    }
    this.currentAddress = address
  }

  setAddressValidationSkipped = (skip: boolean): void => {
    this.addressValidationSkipped = skip
  }

  removeAddressIfNotSupported = (): void => {
    if (this.currentAddress && !(this.currentAddress.countryId in COUNTRIES)) {
      analytics.track('Unsupported address removed', {
        country: this.currentAddress.countryId,
      })
      logger.info(
        'Saved address country is no longer supported, removing it.',
        { countryId: this.currentAddress.countryId }
      )
      this.currentAddress = null
    }
  }

  clearData = (): void => {
    this.addressValidationSkipped = null
    this.currentAddress = null
  }

  setAddressFromServer = (address: NormalizedAddress): void => {
    const denorm = denormalizeAddress(address)
    if (denorm && denorm.countryId) {
      this.setCurrentAddress(denorm as Address, false)
      this.setAddressValidationSkipped(!address.shouldValidate)
    }
  }
}
